import React, { useState } from "react";
import { X, Check } from "lucide-react";

// newPages: [{ id(tmp), pageIndex, thumb }]  (already rendered previews of the 2nd PDF)
export const AddPdfDialog = ({ open, onClose, onConfirm, newPages, totalCurrent, labels }) => {
  const [selected, setSelected] = useState(() => new Set(newPages.map((p) => p.pageIndex)));
  const [position, setPosition] = useState(totalCurrent); // insert index (default: at end)

  React.useEffect(() => {
    setSelected(new Set(newPages.map((p) => p.pageIndex)));
    setPosition(totalCurrent);
  }, [newPages, totalCurrent]);

  if (!open) return null;

  const toggle = (idx) => {
    const s = new Set(selected);
    s.has(idx) ? s.delete(idx) : s.add(idx);
    setSelected(s);
  };
  const allSelected = selected.size === newPages.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(newPages.map((p) => p.pageIndex)));

  const positionOptions = [];
  for (let i = 0; i <= totalCurrent; i++) {
    positionOptions.push({
      value: i,
      label: i === 0 ? labels.atStart : i === totalCurrent ? labels.atEnd : `${labels.afterPage} ${i}`,
    });
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="addpdf-modal">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-slate-900">{labels.title}</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="mb-3 flex items-center justify-between">
          <button onClick={toggleAll} data-testid="addpdf-toggle-all" className="text-sm font-semibold text-emerald-700 hover:underline">
            {allSelected ? labels.deselectAll : labels.selectAll}
          </button>
          <span className="text-sm text-slate-500">{selected.size} / {newPages.length}</span>
        </div>

        <div className="grid flex-1 grid-cols-3 gap-3 overflow-auto rounded-xl bg-slate-50 p-3 sm:grid-cols-4">
          {newPages.map((p) => {
            const on = selected.has(p.pageIndex);
            return (
              <button
                key={p.id}
                onClick={() => toggle(p.pageIndex)}
                data-testid={`addpdf-page-${p.pageIndex}`}
                className={`relative overflow-hidden rounded-lg border-2 bg-white transition-all ${on ? "border-emerald-500 ring-2 ring-emerald-200" : "border-slate-200 opacity-60"}`}
              >
                <img src={p.thumb} alt={`p${p.pageIndex + 1}`} className="h-28 w-full object-contain" />
                <span className="absolute bottom-0 left-0 right-0 bg-slate-900/70 py-0.5 text-center text-[10px] font-semibold text-white">
                  {p.pageIndex + 1}
                </span>
                {on && <Check className="absolute right-1 top-1 h-4 w-4 rounded-full bg-emerald-500 p-0.5 text-white" />}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
            {labels.insertAt}
            <select
              value={position}
              onChange={(e) => setPosition(Number(e.target.value))}
              data-testid="addpdf-position"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-400"
            >
              {positionOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <button
            onClick={() => onConfirm(Array.from(selected).sort((a, b) => a - b), position)}
            disabled={selected.size === 0}
            data-testid="addpdf-confirm"
            className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {labels.insert}
          </button>
        </div>
      </div>
    </div>
  );
};
